import { useCallback, useEffect, useMemo, useState } from 'react';

// Sabit rapor grid'lerinin KULLANICI GÖRÜNÜMÜNÜ kalıcılar: kolon sırası, gizli kolonlar,
// genişlikler, sıralama (ÇOKLU), kolon filtreleri, gruplama, hızlı arama, satır yoğunluğu,
// dondurulmuş (pinned) kolonlar, sayfa boyutu. Her rapor kendi `reportKey`'iyle ayrı saklanır.
//
// Depo şimdilik localStorage'dır. Hook'un okuma/yazma yüzeyi değişmeden sunucuya (kullanıcı
// bazlı "saved views" tablosu) taşınabilir.
const STORAGE_PREFIX = 'reportView.v2.';

function readStored(reportKey) {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + reportKey);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * @param {string} reportKey  Rapora özel kalıcı anahtar (ör. 'visit-analysis').
 * @param {string[]} columnKeys  Rapordaki TÜM kolon anahtarları (varsayılan sıra + geçerlilik).
 */
export function useReportView(reportKey, columnKeys) {
  const defaults = useMemo(() => ({
    order: columnKeys,
    hidden: [],
    widths: {},
    sorting: [],          // [{ id, desc }] — çoklu sıralama
    filters: {},
    grouping: [],         // gruplanan kolon anahtarları (sıralı)
    globalFilter: '',     // hızlı arama
    density: 'comfortable', // 'comfortable' | 'compact'
    pinnedRight: [],      // sağa dondurulan kolonlar (sticky kolon zaten solda sabit)
    cellFormat: {},       // { [colKey]: 'databar' | 'heatmap' } — koşullu biçimlendirme
    // Pivot sekmesinin alan düzeni: { filterFields, colFields, rowFields, valueFields, filterValues }.
    // null = kullanıcı hiç dokunmadı; ReportPivot ilk açılışta kendi varsayılanını üretir.
    pivot: null,
    pageSize: 25,
  }), [columnKeys]);

  const [view, setView] = useState(() => {
    const stored = readStored(reportKey);
    if (!stored) return defaults;
    // Kolon seti değişmiş olabilir: saklı sırayı geçerli anahtarlarla kesiştir, yeni kolonları
    // sona ekle — bozuk/eski görünüm çökertmesin.
    const validOrder = (stored.order || []).filter((k) => columnKeys.includes(k));
    const missing = columnKeys.filter((k) => !validOrder.includes(k));
    const keep = (arr) => (arr || []).filter((k) => columnKeys.includes(k));
    return {
      ...defaults,
      ...stored,
      order: [...validOrder, ...missing],
      hidden: keep(stored.hidden),
      grouping: keep(stored.grouping),
      pinnedRight: keep(stored.pinnedRight),
      sorting: (stored.sorting || []).filter((s) => columnKeys.includes(s.id)),
    };
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_PREFIX + reportKey, JSON.stringify(view));
    } catch {
      /* kota dolu / özel mod — kalıcılık best-effort */
    }
  }, [reportKey, view]);

  const patch = useCallback((p) => setView((v) => ({ ...v, ...p })), []);

  const reset = useCallback(() => {
    setView(defaults);
    try { localStorage.removeItem(STORAGE_PREFIX + reportKey); } catch { /* noop */ }
  }, [defaults, reportKey]);

  // "Görünümü Sıfırla" düğmesi: sıralama/filtre/arama DAHİL herhangi bir özelleştirme var mı?
  const isCustomized = useMemo(
    () => JSON.stringify(view) !== JSON.stringify(defaults),
    [view, defaults],
  );

  // ── Adlı çoklu görünüm (kaydet/seç/sil) ──
  // Anlık "çalışma görünümü" (yukarıdaki `view`) her zaman canlı ve kalıcıdır; adlı görünümler
  // onun ANLIK GÖRÜNTÜLERİDİR. Bir adlı görünüm seçilince state'e yüklenir.
  const [savedViews, setSavedViews] = useState(() => readStoredNamed(reportKey));
  useEffect(() => {
    try { localStorage.setItem(NAMED_PREFIX + reportKey, JSON.stringify(savedViews)); } catch { /* noop */ }
  }, [reportKey, savedViews]);

  const saveCurrentView = useCallback((name) => {
    const trimmed = String(name || '').trim();
    if (!trimmed) return;
    setSavedViews((list) => {
      const existing = list.find((v) => v.name === trimmed);
      const snapshot = JSON.parse(JSON.stringify(view));
      if (existing) return list.map((v) => (v.name === trimmed ? { ...v, state: snapshot } : v));
      return [...list, { id: `${Date.now()}`, name: trimmed, state: snapshot }];
    });
  }, [view]);

  const applyView = useCallback((id) => {
    const v = savedViews.find((x) => x.id === id);
    if (v) setView({ ...defaults, ...v.state });
  }, [savedViews, defaults]);

  const deleteView = useCallback((id) => {
    setSavedViews((list) => list.filter((v) => v.id !== id));
  }, []);

  return { view, patch, reset, isCustomized, savedViews, saveCurrentView, applyView, deleteView };
}

const NAMED_PREFIX = 'reportViews.v1.';
function readStoredNamed(reportKey) {
  try {
    const raw = localStorage.getItem(NAMED_PREFIX + reportKey);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}
